import { get } from "lodash"

const SIZE_VARIABLE_PREFIX = '--col-size-'
export const ROW_SELECTION_COLUMN_WIDTH = 36

export function getColumnSizeVariableName(columnId) {
  const normalizedId = `${columnId}`.replace(/[^a-zA-Z0-9_-]/g, '_')
  return `${SIZE_VARIABLE_PREFIX}${normalizedId}`
}

export function getColumnWidthStyle({ resizable, id, size, maxSize }) {
  if (!resizable) {
    return {}
  }

  const widthFallback = `${Math.min(maxSize, size)}px`
  const widthVariable = `var(${getColumnSizeVariableName(id)}, ${widthFallback})`
  const style = {
    width: `min(${widthVariable}, ${maxSize}px)`,
    maxWidth: `${maxSize}px`,
  }

  return style
}

export function getColumnSizeVariables({ table, columnSizing, hasManualSizing, containerWidth }) {
  const columns = table.getAllLeafColumns().map(column => {
    const sizeFromState = columnSizing[column.id]
    const { size: sizeDef, maxSize } = column.columnDef
    const fallbackSize = sizeDef
    const shouldUseStateSize = typeof sizeFromState === 'number' && column.getCanResize() && hasManualSizing
    const rawSize = shouldUseStateSize ? sizeFromState : fallbackSize
    const size = Math.max(0, Math.min(maxSize, rawSize))
    const isFixedWidth = sizeDef === maxSize
    const reservedWidth = sizeDef

    return {
      id: column.id,
      size,
      maxSize,
      reservedWidth,
      canAutoResize: column.getCanResize() && !isFixedWidth,
    }
  })

  if (!containerWidth || columns.length === 0) {
    return columns.reduce((variables, column) => {
      const clampedSize = Math.max(0, Math.min(column.maxSize, column.size))
      variables[getColumnSizeVariableName(column.id)] = `${clampedSize}px`
      return variables
    }, {})
  }

  const tableUsableWidth = Math.max(containerWidth - ROW_SELECTION_COLUMN_WIDTH, 0)
  const nextColumns = columns.map(column => ({ ...column }))
  const managedColumns = nextColumns
  const totalSize = managedColumns.reduce((sum, column) => sum + column.size, 0)

  if (totalSize > tableUsableWidth) {
    let overflow = totalSize - tableUsableWidth
    let resizableColumns = managedColumns.filter(column => column.canAutoResize)

    while (overflow > 0.5 && resizableColumns.length > 0) {
      const shrinkCapacity = resizableColumns.reduce((sum, column) => sum + Math.max(column.size, 0), 0)
      if (shrinkCapacity <= 0) {
        break
      }

      let reducedInStep = 0
      for (const column of resizableColumns) {
        const capacity = Math.max(column.size, 0)
        if (!capacity) {
          continue
        }

        const reduction = Math.min(capacity, overflow * (capacity / shrinkCapacity))
        column.size -= reduction
        reducedInStep += reduction
      }
      overflow -= reducedInStep

      resizableColumns = resizableColumns.filter(column => column.size > 0.5)
    }
  } else {
    const growableColumns = managedColumns.filter(column => column.canAutoResize && column.size < column.maxSize)
    const freeSpace = tableUsableWidth - totalSize
    if (growableColumns.length > 0) {
      let remainingFreeSpace = freeSpace
      let activeColumns = [...growableColumns]

      while (remainingFreeSpace > 0.5 && activeColumns.length > 0) {
        const extraPerColumn = remainingFreeSpace / activeColumns.length
        let grownInStep = 0

        activeColumns.forEach(column => {
          const capacity = Math.max(column.maxSize - column.size, 0)
          if (!capacity) {
            return
          }

          const growth = Math.min(capacity, extraPerColumn)
          column.size += growth
          grownInStep += growth
        })

        if (grownInStep <= 0) {
          break
        }

        remainingFreeSpace -= grownInStep
        activeColumns = activeColumns.filter(column => column.maxSize - column.size > 0.5)
      }
    }
  }

  return nextColumns.reduce((variables, column) => {
    const clampedSize = Math.max(0, Math.min(column.maxSize, column.size))
    variables[getColumnSizeVariableName(column.id)] = `${clampedSize}px`
    return variables
  }, {})
}

export function parseColumnsToResize(columns) {
  return columns.map((column, index) => {
    const id = column.id || column.dataField || `column-${index}`
    let renderHeader = ''
    if (column.text) {
      if (typeof column.text === 'string') {
        renderHeader = column.text
      } else if (typeof column.text === 'function') {
        renderHeader = column.text('', {}, index)
      } else {
        renderHeader = column.text
      }
    }

    return {
      id,
      accessorFn: row => get(row, column.dataField),
      header: () => renderHeader,
      cell: info => {
        if (column.formatter) {
          return column.formatter(info.getValue(), info.row.original, info.row.index)
        }
        return info.getValue()
      },
      size: typeof column.size === 'number' ? column.size : 150,
      maxSize: typeof column.maxSize === 'number' ? column.maxSize : 1000,
      enableResizing: column.enableResizing ?? column.enableColumnResize ?? true,
      meta: {
        className: column.className,
        headerClassName: column.headerClassName,
        hasExplicitSize: typeof column.size === 'number',
      },
    }
  })
}
